import axios from "axios";  
import { AuthHeader } from "../../helper/auth.token";
import {message} from 'antd';
import { Map, View } from "ol";
import { Projection } from 'ol/proj.js'
import TileLayer from "ol/layer/Tile";
import TileImage from 'ol/source/TileImage';
import TileGrid from 'ol/tilegrid/TileGrid';
import { ScaleLine } from 'ol/control.js';
import { MouseWheelZoom, defaults } from 'ol/interaction';
import { getPrefixedUrl } from '../../utils/utils';

export const getSlideAndInitialiseMapState = (value, setState, urlState, updateUrlState)=> {
    setState({
        isFetching: true
    })
    let url = `/api/slide/${value}/`;
    axios
      .get(url, { headers: { Authorization: AuthHeader() } })
      .then(response => {
        if (response.status === 200) {
            initialiseSlideMap(response.data, setState, urlState, updateUrlState);
        } else {
          setState({
              isErrored: true,
              isFetching: false, 
              errMessage: response.data
          });
        }
      })
      .catch(err => {
        message.error("Slide could not be found. Contact Admin.");
        setState({
            isErrored: true,
            isFetching: false, 
            errMessage: err
        });
   });
}

const initialiseSlideMap = (slide_data, setState, urlState, updateUrlState) => {

    let tileSize;
    if (slide_data.specimen_type === "blood") {
        tileSize = [slide_data.tile_width, slide_data.tile_height];
    } else {
        tileSize = [slide_data.stitched_tile_width, slide_data.stitched_tile_height];
    }

    let urlSuffix;
    if (slide_data.specimen_type === "blood") {
        urlSuffix = `${slide_data.bucket_name}/${slide_data.path}tiled/{z}/x{x}y{y}.` + slide_data.img_type;
    } else {
        urlSuffix = `${slide_data.bucket_name}/${slide_data.path}stitched/{z}/x{x}y{y}.` + slide_data.img_type;
    }

    let zoomLevels = getZoomLevels(slide_data);
    let resolutions = getResolutions(slide_data, zoomLevels);
    let imageShape = getImageShape(slide_data);
    let projection = getProjection(imageShape);
    let view = getView(projection, resolutions, urlState, imageShape, slide_data.viewer_rotation);
    let url = getPrefixedUrl(urlSuffix, slide_data);
    let layer = getLayer(tileSize, projection, resolutions, url);
    let slidemap = getMap(view, layer, true);
    let maxZoom = zoomLevels.length - 1;
    let zoomScale =  getZoomScale(slide_data, maxZoom);
    slidemap.addControl(
        new ScaleLine({
            units: "metric",
            minWidth: 100, 
            // className: "map-scale"
        })
    );

    slidemap.addEventListener('moveend', updateUrlState, false);
    
    setState({
            isFetching: false,
            slide_data, 
            maxZoom, 
            zoomLevels,
            zoomScale, 
            view, 
            layer, 
            slidemap
        });
}

const getImageShape = (slide_data) => {
    let imageWidth, imageHeight;

    if (slide_data.specimen_type === "blood") {
        imageWidth = slide_data.uperpixel * slide_data.tile_width * slide_data.x_fields;
        imageHeight = slide_data.uperpixel * slide_data.tile_height * slide_data.y_fields;
    } else {
        imageWidth = slide_data.uperpixel * slide_data.stitched_tile_width * slide_data.stitched_x_max;
        imageHeight = slide_data.uperpixel * slide_data.stitched_tile_height * slide_data.stitched_y_max;
    }

    return [imageWidth, imageHeight];
}

export const getProjection = (imageShape) => {

    let projection = new Projection({
        code: 'MORPHLE',
        units: 'microns',
        extent: [0, 0, imageShape[0], imageShape[1]],
        metersPerUnit: 0.000001,
        global: true,
        getPointResolution: (resolution, point) => resolution,
    });

    return projection;
}

export const getView = (projection, resolutions, initStateWith, imageShape, viewer_rotation) => {
    
    let view = new View({
        projection: projection,
        extent: projection.getExtent(),
        center: initStateWith.x === -1 ? imageShape : [initStateWith.x, initStateWith.y],
        zoom: initStateWith.z,
        maxResolution: resolutions[0],
        maxZoom: initStateWith.digitalZoomStatus ? (resolutions.length - 1) : (resolutions.length - 2),
        rotation: initStateWith.r != -1 ? initStateWith.r : ((viewer_rotation * Math.PI) / 180)
    });

    return view;
}

export const getLayer = (tileSize, projection, resolutions, url) => {

    let layer = new TileLayer({
        extent: projection.getExtent(),
        renderMode: "vector",
        source: new TileImage({
            tileGrid: new TileGrid({
                extent: projection.getExtent(),
                origin: [0, projection.getExtent()[3]],
                resolutions: resolutions,
                tileSize: tileSize,
            }),
            projection: projection,
            url: url,
            wrapX: false,
            crossOrigin: 'anonymous'
        }),
    });

    return layer;
}

export const getMap = (view, layer, setKeyboardInteractions) => {

    let slidemap = new Map({
        controls: [],
        interactions: defaults({mouseWheelZoom: false}).extend([
            new MouseWheelZoom({
              constrainResolution: true // force zooming to a integer zoom
            })
          ]),
        target: null,
        layers: [
            layer,
        ],
        view: view,
        keyboardEventTarget: setKeyboardInteractions ? document : null,
        loadTilesWhileAnimating: true,
        loadTilesWhileInteracting: true
    });

    return slidemap;
}

const getZoomLevels = (slide_data) => {

    let zoomLevels = [];
    if (slide_data.specimen_type === "blood") {
        zoomLevels = slide_data.tiling_z_levels.split(",");
    } else {
        zoomLevels = slide_data.z_levels.split(",");
    }

    return zoomLevels;
}

export const getResolutions = (slide_data, zoomLevels) => {

    let resolutions = [];
    (zoomLevels).forEach((level, index) => {
        resolutions.push(slide_data.uperpixel * Math.pow(2, parseInt(level)));
    });

    resolutions = resolutions.reverse();

    return resolutions;
}

export const getZoomScale = (slide_data, max_zoom) => {
    var scale = [];
    let max = (0) + parseInt(max_zoom);
    let i = max - 1;
    if (slide_data.objective_type === "hundred_x") {
	    scale[max] = 200;
    } else if (slide_data.objective_type === "twenty_x") {
	    scale[max] = 40;
    } else if (slide_data.objective_type === "sixty_x") {
	    scale[max] = 120;
    } else
    {
      scale[max] = 80;
    }
    for (i; i > -1; i--) {
      scale[i] = parseFloat((scale[i + 1] / 2).toFixed(1));
    }
    return scale;
}
